/**
 * API для кластеров услуг и канонических preset-кластеров.
 * Дата: 17.03.2026
 */
import { DELIVERY_CHANNEL_IDS, getChannelDisplayName, normalizeChannelId } from '@/config/channels';
import { apiClient } from './client';

export interface ServiceCluster {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
  registry_key?: string;
  registry_version?: number;
  is_registry_managed: boolean;
  datalayer_cluster_external_id?: string;
  sync_origin?: string;
  datalayer_updated_at?: string;
  last_synced_at?: string;
  local_manual_override_at?: string;
  service_count?: number;
  template_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ServiceClusterMember {
  id: string;
  cluster_id: string;
  service_id: string;
  added_by?: string;
  sort_order: number;
  is_active: boolean;
  membership_source?: string;
  match_reason?: string;
  datalayer_service_external_id?: string;
  sync_origin?: string;
  datalayer_updated_at?: string;
  last_synced_at?: string;
  local_manual_override_at?: string;
  added_at: string;
  service?: {
    id: string;
    external_id: string;
    name: string;
    category_id?: string;
    source_system?: string;
    source_data?: Record<string, unknown>;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
}

export interface ServiceClusterTemplate {
  id: string;
  cluster_id: string;
  template_id: string;
  trigger_type: 'after_completed' | 'after_cancelled' | 'manual';
  channel?: string;
  delay_hours: number;
  dedup_scope: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClusterTriggerBindingSlot {
  channel?: string;
  label: string;
  has_binding: boolean;
  mapping_active: boolean;
  warning?: string;
  template?: {
    id: string;
    name: string;
    type: string;
    category?: string;
    is_active: boolean;
    priority: number;
    delay_hours: number;
    dedup_scope?: string;
  };
}

export interface ClusterTriggerBindingItem {
  trigger_type: 'after_completed' | 'after_cancelled' | 'manual';
  label: string;
  dispatch_mode?: 'auto' | 'manual';
  generic_binding: ClusterTriggerBindingSlot;
  channel_bindings: ClusterTriggerBindingSlot[];
}

export interface ClusterTriggerBindingsResponse {
  trigger_types: Array<{ trigger_type: string; label: string }>;
  channels: Array<{ channel: string; label: string }>;
  bindings: ClusterTriggerBindingItem[];
  total: number;
  is_legacy_fallback?: boolean;
}

export interface CreateClusterRequest {
  name: string;
  slug?: string;
  description?: string;
  color?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
}

export interface UpdateClusterRequest {
  name?: string;
  slug?: string;
  description?: string;
  color?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface AddServiceRequest {
  service_id: string;
  sort_order?: number;
}

export interface ReorderServicesRequest {
  services: Array<{
    service_id: string;
    sort_order: number;
  }>;
}

export interface LinkTemplateRequest {
  template_id: string;
  trigger_type: 'after_completed' | 'after_cancelled' | 'manual';
  channel?: string;
  delay_hours?: number;
  dedup_scope?: string;
  priority?: number;
  is_active?: boolean;
}

export interface ClusterPreset {
  key: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  display_order: number;
  version: number;
  include_canonical_types?: string[];
  include_patterns?: string[];
  exclude_patterns?: string[];
}

export interface PreviewPresetService {
  service: {
    id: string;
    external_id: string;
    name: string;
    source_data?: Record<string, unknown>;
  };
  canonical_type?: string;
  match_reason: string;
  already_in_cluster: boolean;
  overlap_registry_keys?: string[];
  will_add: boolean;
}

export interface PreviewPresetSummary {
  preset: ClusterPreset;
  existing_cluster?: ServiceCluster;
  matched_services: PreviewPresetService[];
  matched_count: number;
  existing_member_count: number;
  will_add_count: number;
  overlap_count: number;
  will_create_cluster: boolean;
}

export interface PreviewPresetsResponse {
  presets: PreviewPresetSummary[];
  selected_preset_keys: string[];
  matched_services_total: number;
  will_create_clusters: number;
  will_add_memberships: number;
  overlap_services_total: number;
}

export interface ApplyPresetsResponse {
  preview: PreviewPresetsResponse;
  created_clusters: number;
  updated_clusters: number;
  added_memberships: number;
}

export interface PreviewPresetsRequest {
  preset_keys?: string[];
  include_inactive_services?: boolean;
}

export interface ServiceClusterSyncResult {
  tenant_id: string;
  tenant_slug: string;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  duration: string;
  synced_at: string;
  error_message?: string;
}

function withTenantParams(params: Record<string, unknown>, tenantId?: string) {
  if (tenantId) {
    return { ...params, tenant_id: tenantId };
  }
  return params;
}

function normalizeBindingSlot(slot?: Partial<ClusterTriggerBindingSlot>, fallback?: Partial<ClusterTriggerBindingSlot>): ClusterTriggerBindingSlot {
  return {
    channel: slot?.channel ?? fallback?.channel,
    label: slot?.label || fallback?.label || '',
    has_binding: slot?.has_binding ?? fallback?.has_binding ?? false,
    mapping_active: slot?.mapping_active ?? fallback?.mapping_active ?? false,
    warning: slot?.warning ?? fallback?.warning,
    template: slot?.template ?? fallback?.template,
  };
}

const CLUSTER_TRIGGER_TYPES: ClusterTriggerBindingsResponse['trigger_types'] = [
  { trigger_type: 'after_completed', label: 'После завершения приёма' },
  { trigger_type: 'after_cancelled', label: 'После отмены записи' },
  { trigger_type: 'manual', label: 'Ручной запуск' },
];

const CLUSTER_TRIGGER_CHANNELS: ClusterTriggerBindingsResponse['channels'] = DELIVERY_CHANNEL_IDS.map((channel) => ({
  channel,
  label: getChannelDisplayName(channel),
}));

function buildLegacyClusterTriggerBindingsResponse(templates: ServiceClusterTemplate[]): ClusterTriggerBindingsResponse {
  const activeTemplates = [...templates]
    .filter((template) => template.is_active)
    .sort((a, b) => {
      if (a.trigger_type !== b.trigger_type) {
        return a.trigger_type.localeCompare(b.trigger_type);
      }
      if ((a.channel || '') !== (b.channel || '')) {
        return (a.channel || '').localeCompare(b.channel || '');
      }
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      if (a.delay_hours !== b.delay_hours) {
        return a.delay_hours - b.delay_hours;
      }
      return (a.created_at || '').localeCompare(b.created_at || '');
    });

  const templatesByKey = new Map<string, ServiceClusterTemplate>();
  activeTemplates.forEach((template) => {
    const channel = normalizeChannelId(template.channel || '');
    const key = `${template.trigger_type}:${channel}`;
    if (!templatesByKey.has(key)) {
      templatesByKey.set(key, { ...template, channel });
    }
  });

  const bindings: ClusterTriggerBindingItem[] = CLUSTER_TRIGGER_TYPES.map((triggerType) => {
    const genericTemplate = templatesByKey.get(`${triggerType.trigger_type}:`);
    return {
      trigger_type: triggerType.trigger_type as ClusterTriggerBindingItem['trigger_type'],
      label: triggerType.label,
      generic_binding: normalizeBindingSlot(
        genericTemplate
          ? {
              label: 'Общая generic-привязка',
              has_binding: true,
              mapping_active: true,
              template: {
                id: genericTemplate.template_id,
                name: '',
                type: '',
                category: '',
                is_active: true,
                priority: genericTemplate.priority || 0,
                delay_hours: genericTemplate.delay_hours || 0,
                dedup_scope: genericTemplate.dedup_scope || 'per_appointment',
              },
            }
          : {
              label: 'Общая generic-привязка',
              has_binding: false,
              mapping_active: false,
              warning: 'Нет общей generic-привязки',
            }
      ),
      channel_bindings: CLUSTER_TRIGGER_CHANNELS.map((channel) => {
        const template = templatesByKey.get(`${triggerType.trigger_type}:${channel.channel}`);
        return normalizeBindingSlot(
          template
            ? {
                channel: channel.channel,
                label: channel.label,
                has_binding: true,
                mapping_active: true,
                template: {
                  id: template.template_id,
                  name: '',
                  type: '',
                  category: '',
                  is_active: true,
                  priority: template.priority || 0,
                  delay_hours: template.delay_hours || 0,
                  dedup_scope: template.dedup_scope || 'per_appointment',
                },
              }
            : {
                channel: channel.channel,
                label: channel.label,
                has_binding: false,
                mapping_active: false,
                warning: 'Будет использована generic-привязка',
              }
        );
      }),
    };
  });

  return {
    trigger_types: CLUSTER_TRIGGER_TYPES,
    channels: CLUSTER_TRIGGER_CHANNELS,
    bindings,
    total: bindings.length,
    is_legacy_fallback: true,
  };
}

function normalizeClusterTriggerBindingsResponse(data: any): ClusterTriggerBindingsResponse {
  const channels = Array.isArray(data?.channels) ? data.channels : [];
  const bindings = Array.isArray(data?.bindings)
    ? data.bindings.map((binding: any) => ({
        trigger_type: binding?.trigger_type || '',
        label: binding?.label || binding?.trigger_type || '',
        dispatch_mode: binding?.dispatch_mode === 'auto' || binding?.dispatch_mode === 'manual'
          ? binding.dispatch_mode
          : binding?.trigger_type === 'manual'
            ? 'manual'
            : undefined,
        generic_binding: binding?.generic_binding
          ? normalizeBindingSlot(binding.generic_binding, { label: 'Общая generic-привязка' })
          : normalizeBindingSlot({ label: 'Общая generic-привязка' }),
        channel_bindings: Array.isArray(binding?.channel_bindings)
          ? binding.channel_bindings.map((slot: any) => normalizeBindingSlot(slot))
          : channels.map((channel: { channel: string; label: string }) =>
              normalizeBindingSlot({
                channel: channel.channel,
                label: channel.label,
                warning: 'Будет использована generic-привязка',
              })
            ),
      }))
    : [];

  return {
    trigger_types: Array.isArray(data?.trigger_types) ? data.trigger_types : [],
    channels,
    bindings,
    total: typeof data?.total === 'number' ? data.total : bindings.length,
  };
}

export const serviceClustersApi = {
  list: async (includeInactive = false, tenantId?: string): Promise<{ clusters: ServiceCluster[] }> => {
    const { data } = await apiClient.get('/service-clusters', {
      params: withTenantParams({ include_inactive: includeInactive }, tenantId),
    });
    return data;
  },

  get: async (id: string, tenantId?: string): Promise<ServiceCluster> => {
    const { data } = await apiClient.get(`/service-clusters/${id}`, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  create: async (payload: CreateClusterRequest, tenantId?: string): Promise<ServiceCluster> => {
    const { data } = await apiClient.post('/service-clusters', payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  update: async (id: string, payload: UpdateClusterRequest, tenantId?: string): Promise<ServiceCluster> => {
    const { data } = await apiClient.put(`/service-clusters/${id}`, payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  delete: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.delete(`/service-clusters/${id}`, {
      params: withTenantParams({}, tenantId),
    });
  },

  getServices: async (clusterId: string, tenantId?: string): Promise<{ services: ServiceClusterMember[] }> => {
    const { data } = await apiClient.get(`/service-clusters/${clusterId}/services`, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  addService: async (clusterId: string, payload: AddServiceRequest, tenantId?: string): Promise<{ status: string }> => {
    const { data } = await apiClient.post(`/service-clusters/${clusterId}/services`, payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  removeService: async (clusterId: string, serviceId: string, tenantId?: string): Promise<{ status: string }> => {
    const { data } = await apiClient.delete(`/service-clusters/${clusterId}/services/${serviceId}`, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  reorderServices: async (clusterId: string, payload: ReorderServicesRequest, tenantId?: string): Promise<{ status: string }> => {
    const { data } = await apiClient.put(`/service-clusters/${clusterId}/services/reorder`, payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  getTemplates: async (clusterId: string, tenantId?: string): Promise<{ templates: ServiceClusterTemplate[] }> => {
    const { data } = await apiClient.get(`/service-clusters/${clusterId}/templates`, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  listTriggerBindings: async (clusterId: string, tenantId?: string): Promise<ClusterTriggerBindingsResponse> => {
    try {
      const { data } = await apiClient.get(`/service-clusters/${clusterId}/trigger-bindings`, {
        params: withTenantParams({}, tenantId),
      });
      return normalizeClusterTriggerBindingsResponse(data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const templatesData = await serviceClustersApi.getTemplates(clusterId, tenantId);
        return buildLegacyClusterTriggerBindingsResponse(templatesData.templates || []);
      }
      throw error;
    }
  },

  setTriggerBinding: async (
    clusterId: string,
    triggerType: string,
    templateId: string,
    channel?: string,
    tenantId?: string,
    options?: { activateTemplate?: boolean; priority?: number; delayHours?: number; dedupScope?: string }
  ): Promise<{ message: string; warnings?: string[] }> => {
    const { data } = await apiClient.post(
      `/service-clusters/${clusterId}/trigger-bindings`,
      {
        trigger_type: triggerType,
        template_id: templateId,
        channel,
        activate_template: options?.activateTemplate,
        priority: options?.priority,
        delay_hours: options?.delayHours,
        dedup_scope: options?.dedupScope,
      },
      { params: withTenantParams({}, tenantId) }
    );
    return data;
  },

  clearTriggerBinding: async (clusterId: string, triggerType: string, channel?: string, tenantId?: string): Promise<{ message: string }> => {
    const { data } = await apiClient.delete(`/service-clusters/${clusterId}/trigger-bindings/${triggerType}`, {
      params: withTenantParams(channel ? { channel } : {}, tenantId),
    });
    return data;
  },

  linkTemplate: async (clusterId: string, payload: LinkTemplateRequest, tenantId?: string): Promise<ServiceClusterTemplate> => {
    const { data } = await apiClient.post(`/service-clusters/${clusterId}/templates`, payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  unlinkTemplate: async (
    clusterId: string,
    templateId: string,
    triggerType: 'after_completed' | 'after_cancelled' | 'manual',
    tenantId?: string
  ): Promise<{ status: string }> => {
    const { data } = await apiClient.delete(`/service-clusters/${clusterId}/templates/${templateId}`, {
      params: withTenantParams({ trigger_type: triggerType }, tenantId),
    });
    return data;
  },

  manualTrigger: async (clusterId: string, appointmentId: string, tenantId?: string): Promise<{ status: string }> => {
    const { data } = await apiClient.post(
      `/service-clusters/${clusterId}/manual-trigger`,
      { appointment_id: appointmentId },
      { params: withTenantParams({}, tenantId) }
    );
    return data;
  },

  listPresets: async (tenantId?: string): Promise<{ presets: ClusterPreset[] }> => {
    const { data } = await apiClient.get('/service-cluster-presets', {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  previewPresets: async (payload: PreviewPresetsRequest, tenantId?: string): Promise<PreviewPresetsResponse> => {
    const { data } = await apiClient.post('/service-cluster-presets/preview', payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  applyPresets: async (payload: PreviewPresetsRequest, tenantId?: string): Promise<ApplyPresetsResponse> => {
    const { data } = await apiClient.post('/service-cluster-presets/apply', payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },

  sync: async (
    payload: { limit?: number; full_sync?: boolean } = {},
    tenantId?: string
  ): Promise<{ success: boolean; message: string; result: ServiceClusterSyncResult }> => {
    const { data } = await apiClient.post('/sync/service-clusters', payload, {
      params: withTenantParams({}, tenantId),
    });
    return data;
  },
};
